import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { restoreSession } from '@utils/sessionRestore'
import { applySessionRestoreResult } from '@utils/applySessionRestoreResult'

function readStoredAccessToken(): string | null {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

/**
 * On app startup, validates or silently refreshes stored credentials so
 * "Keep me signed in" survives browser restarts and backend cold starts.
 */
export function useStartupTokenRefresh(): void {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { token, rememberMe } = useAppSelector((state) => state.auth)
  const hasRun = useRef(false)
  const [restoreDeferred, setRestoreDeferred] = useState(false)

  const attemptRestore = useCallback(async () => {
    const accessToken = token ?? readStoredAccessToken()

    const result = await restoreSession(accessToken)
    const outcome = applySessionRestoreResult(result, dispatch, {
      rememberMe,
      navigateToLogin: () => navigate('/auth/login', { replace: true }),
    })
    setRestoreDeferred(outcome === 'unavailable')
  }, [token, rememberMe, dispatch, navigate])

  useEffect(() => {
    if (hasRun.current) return
    hasRun.current = true
    void attemptRestore()
  }, [attemptRestore])

  useEffect(() => {
    if (!restoreDeferred) return

    const retry = () => {
      if (document.visibilityState === 'visible') {
        void attemptRestore()
      }
    }

    window.addEventListener('focus', retry)
    document.addEventListener('visibilitychange', retry)
    return () => {
      window.removeEventListener('focus', retry)
      document.removeEventListener('visibilitychange', retry)
    }
  }, [restoreDeferred, attemptRestore])
}
